import { useCallback, useState } from 'react';

import DrawerTitle from '~/domains/design-system/components/Drawer/DrawerTitle';
import Modal from '~/domains/common/components/Modal/Modal/Modal';
import { Scroller } from '~/domains/design-system/components/Scroller/Scroller';

import FilterContentSwitch from './FilterContentSwitch/FilterContentSwitch';
import type { TFilter } from './types';
import useStyles from './Filters.styles';
import FilterButton from './FilterButton';

type FilterValues<VTS extends readonly TFilter[]> = {
  [K in VTS[number]['key']]: VTS[number] extends TFilter<infer VT> ? VT[] : never;
};

type FiltersProps<F extends readonly TFilter[]> = {
  children?: React.ReactNode;
  filters: F;
  onChange: (values: FilterValues<F>) => void;
  values: FilterValues<F>;
  hide?: boolean;
};

function Filters<F extends readonly TFilter[]>({
  children,
  filters,
  onChange,
  values,
  hide,
}: FiltersProps<F>) {
  const { classes, theme } = useStyles();
  const [tempValues, setTempValues] = useState<FilterValues<F>>(values);
  const [opennedFilter, setOpennedFilter] = useState<TFilter | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleChange = useCallback(
    (value: unknown) => {
      setTempValues((current) => ({ ...current, [opennedFilter?.key]: value }));
    },
    [opennedFilter?.key]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => (filterKey: string) => {
      e.stopPropagation();
      setTempValues((current) => ({ ...current, [filterKey]: undefined }));
      onChange({ ...tempValues, [filterKey]: undefined });
    },
    [tempValues]
  );

  const handleClose = useCallback(
    (value?: unknown) => {
      setIsDrawerOpen(false);
      if (!value) {
        onChange(tempValues);
        return;
      }
      handleChange(value);
      onChange({
        ...tempValues,
        [opennedFilter?.key]: value,
      });
    },
    [handleChange, onChange, opennedFilter?.key, tempValues]
  );

  if (hide) return null;

  return (
    <>
      <Scroller backgroundColor={theme.colors.gray2[0]} fillWidth={Boolean(children)}>
        {filters.map((filter) => (
          <>
            <FilterButton
              filter={filter}
              setOpennedFilter={setOpennedFilter}
              setIsDrawerOpen={setIsDrawerOpen}
              clearFilters={handleClear}
            />
            {filter.key === 'SORT_BY' && <div className={classes.divider} />}
          </>
        ))}
        {children}
      </Scroller>
      <Modal
        onClose={() => {
          handleClose();
        }}
        isOpened={isDrawerOpen}
        classNames={{
          modal: { body: classes.modalBody },
          drawer: { body: classes.drawerBody },
        }}
      >
        <div className={classes.modalContent}>
          <DrawerTitle>{opennedFilter?.title}</DrawerTitle>
          {opennedFilter && (
            <FilterContentSwitch
              filter={opennedFilter}
              onChange={handleChange}
              onSubmit={handleClose}
              onClear={handleClear}
              value={tempValues[opennedFilter.key]}
            />
          )}
        </div>
      </Modal>
    </>
  );
}

export default Filters;
